import {
  AbstractControl,
  FormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms'
import { splitData } from '@helios/utils'
import _ from 'lodash-es'

/**
 * Function to create a list validator.
 *
 * This function takes validators as an input. During validation, it splits the
 * input into a list and applies those validators to each element of the list.
 * This supports our standard delimiters (commas, semicolons, tabs, spaces, tabs, newlines, and carriage returns).
 * Delimiters can be mixed and matched freely, this validator does not care.
 *
 * Warning: Element validator functions should only access the control's value property. Otherwise, things will get messy.
 */
export function ListValidator(...validators: ValidatorFn[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const validator = Validators.compose(validators)
    if (validator === null) {
      return null
    }

    const input: (string | number)[] | string = control?.value

    // Check that the input is some sort of list the validator can process
    const isInputString = typeof input === 'string'
    const isInputArray =
      Array.isArray(input) &&
      input.every(x => typeof x === 'string' || typeof x === 'number')
    const isInputEmpty =
      (Array.isArray(input) || typeof input === 'string') && input.length === 0
    if (
      input === undefined ||
      !(isInputString || isInputArray) ||
      isInputEmpty
    ) {
      return null
    }

    // Get a list from the input, splitting it when the input is string
    const valueList: (number | string)[] = Array.isArray(input)
      ? input // simply process the list
      : splitData(input) // split strings

    // Create some fake controls to pass to validators.
    // This is safe, because validators never access any other property of a control
    const elementControls = valueList.map(value => ({ value } as FormControl))

    let errors = {}
    for (const elementControl of elementControls) {
      const elementErrors = validator(elementControl)
      errors = { ...elementErrors, ...errors }
    }

    return _.isEmpty(errors) ? null : errors
  }
}
